import {
  Tabs,
  Button,
  Collapse,
  Flex,
  Input,
  Select,
  Tooltip,
  Typography,
} from 'antd'
import { ProblemDescription } from './ProblemDescription'
import { PossibleRootCauses } from './PossibleRootCauses'
import { DirectRootCauses } from './DirectRootCauses'
import { Actions } from './Actions'
import { useEffect, useState } from 'react'
import { FaultDetails } from './FaultDetails'
import { isEmpty } from 'lodash'
import { useHistory } from 'react-router-dom'
import {
  useFetchIssueCategories,
  useIsCompressedIssueCreationInterface,
  useLinkProblemLogToIssue,
  usePostIssue,
  useUpdateIssue,
} from '../hooks/Issues'
import { useGetUsers, useUserInfo } from '../hooks/User'
import { filterOptionByLabel } from '../helpers/utils'

const { Text } = Typography

// This is the left side of the figma (it also occasionally appears on the right).
// https://www.figma.com/proto/ITEOBzG70wVkwRkep3rc0B/Tracer---Ant-Design?page-id=9316%3A23734&node-id=9613-21823&node-type=frame&viewport=-64%2C334%2C0.12&t=KPS4u8Suia3eGyRO-1&scaling=contain&content-scaling=fixed&starting-point-node-id=9607%3A15334
// This has all of the inputs and form functionality for the various parts of the issue
export const IssueEditForm = ({
  issue,
  setIssue,
  fault,
  orientation,
  readOnly,
  showFaultDetails,
  intl,
  setFault,
}) => {
  let history = useHistory()
  const isCompressed = useIsCompressedIssueCreationInterface()
  const [activeTab, setActiveTab] = useState('problem-desc')
  const postIssue = usePostIssue()
  const updateIssue = useUpdateIssue()
  const linkProblemLogToIssue = useLinkProblemLogToIssue()
  const makeModelId = fault?.make_model_id ?? null
  const { categories } = useFetchIssueCategories({ make_model_id: makeModelId })
  const categoryOptions = (categories ?? []).map(
    ({ id, code, description }) => ({
      value: id,
      label: `${code} — ${description}`,
    }),
  )
  const { users } = useGetUsers()
  const { userInfo } = useUserInfo()
  const responsibleOptions = (users ?? [])
    .filter((user) => !user.disabled && !isEmpty(user.email))
    .map((user) => ({ value: user.email, label: user.email }))

  useEffect(() => {
    if (!issue?.id && !issue?.responsible && userInfo) {
      setIssue({ ...issue, responsible: userInfo.username })
    }
  }, [issue?.id, issue?.responsible, userInfo])

  let items = []
  if (showFaultDetails && !!fault) {
    items.push({
      key: 'fault-details',
      label: intl.formatMessage({
        id: 'incidentMgmt.issueEditForm.faultDetails',
        defaultMessage: 'Fault Details',
      }),
      children: (
        <FaultDetails
          fault={fault}
          intl={intl}
          setFault={setFault}
          issue={issue}
        />
      ),
    })
  }

  // 'compressed' trims this down to just the 4M + Actions tabs, matching the
  // Figma-designed Add New Issue page -- Problem Description and Direct Root
  // Causes are not editable in this mode. See ISSUE_CREATION_INTERFACE.
  if (!isCompressed) {
    items.push({
      key: 'problem-desc',
      label: intl.formatMessage({
        id: 'incidentMgmt.issueEditForm.problemDescription',
        defaultMessage: 'Problem Description',
      }),
      children: (
        <ProblemDescription
          issue={issue}
          setIssue={setIssue}
          readOnly={readOnly}
          intl={intl}
        />
      ),
    })
  }

  items.push({
    key: 'possible-root-causes',
    label: isCompressed
      ? intl.formatMessage({
          id: 'incidentMgmt.issueEditForm.fourM',
          defaultMessage: '4M',
        })
      : intl.formatMessage({
          id: 'incidentMgmt.issueEditForm.possibleRootCauses',
          defaultMessage: 'Possible Root Causes',
        }),
    children: (
      <PossibleRootCauses
        issue={issue}
        setIssue={setIssue}
        readOnly={readOnly}
        intl={intl}
      />
    ),
  })

  if (!isCompressed) {
    items.push({
      key: 'direct-root-cause',
      label: intl.formatMessage({
        id: 'incidentMgmt.issueEditForm.directRootCauses',
        defaultMessage: 'Direct Root Causes',
      }),
      children: (
        <DirectRootCauses
          issue={issue}
          setIssue={setIssue}
          readOnly={readOnly}
          intl={intl}
        />
      ),
    })
  }

  items.push({
    key: 'actions',
    label: intl.formatMessage({
      id: 'incidentMgmt.issueEditForm.actions',
      defaultMessage: 'Actions',
    }),
    children: (
      <Actions
        issue={issue}
        setIssue={setIssue}
        readOnly={readOnly}
        intl={intl}
      />
    ),
  })

  const isHorizontal = orientation !== 'vertical'

  const tabKeys = items.map((item) => item.key)

  // isCompressed loads asynchronously (from userInfo) and can change which
  // tab keys exist -- fall back to the first tab if activeTab no longer
  // matches one, rather than rendering with no tab selected.
  const currentTab = tabKeys.includes(activeTab) ? activeTab : tabKeys[0]

  const advanceTab = (direction) => {
    // direction is +1 or -1, +1 to go right, -1 to go left
    const currentIdx = tabKeys.indexOf(currentTab)
    let newIdx = currentIdx + direction
    if (newIdx >= tabKeys.length) {
      // went past the right tab
      newIdx = newIdx % tabKeys.length
    } else if (newIdx < 0) {
      // went past the left tab
      newIdx = tabKeys.length + newIdx
    }
    const newActiveTab = tabKeys[newIdx]
    setActiveTab(newActiveTab)
  }

  const goToIncidentsPage = () => {
    const url = `/incidents`
    history.push(url)
  }

  // Reached from wherever navigateToIncidentMgmtPage() was called (Fault
  // Reports' "+" button for a new issue, or Incident List/Issue List for an
  // existing one) -- that's a real browser navigation (window.open(..,
  // '_self')), so it leaves a genuine history entry to go back to. Only
  // fall back to the Incident List when there's nothing to go back to, e.g.
  // this page was opened directly (a bookmark, a shared link, a fresh tab).
  const cancel = () => {
    if (history.length > 1) {
      history.goBack()
    } else {
      history.push('/incidents?tab=incident-list')
    }
  }
  // Either post a new issue (if no issue id) or
  // update existing. Also link fault to issue,
  // then redirect to incidents page
  const submitIssue = () => {
    const attachProbsToIssue = ({ issue_id }) => {
      let problemsToAttach
      if (fault.problems) {
        problemsToAttach = fault.problems
      } else {
        problemsToAttach = [fault]
      }
      Promise.all(
        problemsToAttach
          .filter((prb) => !prb.attachedIssue)
          .map((prb) => {
            return linkProblemLogToIssue.mutateAsync({
              issue_id,
              pl_id: prb.id,
            })
          }),
      ).then(() => goToIncidentsPage())
    }
    const shouldUpdate = !!issue.id
    let issueReq
    if (shouldUpdate) {
      updateIssue.mutate(
        { issue_id: issue.id, issue },
        {
          onSuccess: (issue_id) => {
            attachProbsToIssue(issue_id)
          },
        },
      )
    } else {
      postIssue.mutate(
        { issue },
        {
          onSuccess: (issue_id) => {
            attachProbsToIssue(issue_id)
          },
        },
      )
    }
  }

  // A category is required to create a new issue (matching AddNewIssue.js,
  // which blocks entry to the page entirely without one) -- issues reached
  // via this form don't have a category picked ahead of time, so it's
  // enforced here instead, on submit, once one is created.
  const isNewIssue = !issue?.id
  const needsCategory = isNewIssue && !issue?.issue_category_id
  const finishDisabledReason = needsCategory
    ? intl.formatMessage({
        id: 'incidentMgmt.issueEditForm.needsCategory',
        defaultMessage: 'Select a category before creating an issue',
      })
    : undefined

  const getButtons = () => {
    return (
      <Flex id="tab-controls" vertical={false} gap={10}>
        {currentTab !== 'actions' && (
          <Button type="primary" onClick={() => advanceTab(1)}>
            {intl.formatMessage({
              id: 'incidentMgmt.issueEditForm.next',
              defaultMessage: 'Next',
            })}
          </Button>
        )}
        {}
        {currentTab !== tabKeys[0] && (
          <Button type="default" onClick={() => advanceTab(-1)}>
            {intl.formatMessage({
              id: 'incidentMgmt.issueEditForm.previous',
              defaultMessage: 'Previous',
            })}
          </Button>
        )}
        {!readOnly && (
          <>
            {' '}
            {/* Tooltip wraps a span, not the Button directly -- antd disabled
                buttons don't reliably fire the hover events Tooltip listens for. */}
            <Tooltip title={finishDisabledReason}>
              <span>
                <Button
                  id="issue-finish-btn"
                  type="primary"
                  disabled={needsCategory}
                  onClick={() => submitIssue()}
                >
                  {intl.formatMessage({
                    id: 'incidentMgmt.issueEditForm.finish',
                    defaultMessage: 'Finish',
                  })}
                </Button>
              </span>
            </Tooltip>
            <Button id="issue-cancel-button" onClick={() => cancel()} danger>
              {intl.formatMessage({
                id: 'incidentMgmt.issueEditForm.cancel',
                defaultMessage: 'Cancel',
              })}
            </Button>
          </>
        )}
      </Flex>
    )
  }
  return (
    <Flex
      id="issue-edit-form"
      className={isHorizontal ? 'issue-edit-horizontal' : 'issue-edit-vertical'}
      vertical={true}
      gap={10}
    >
      {!isEmpty(issue) && categoryOptions.length > 0 && (
        <Flex vertical={false} align="center" gap="small">
          <Text strong>
            {intl.formatMessage({
              id: 'incidentMgmt.issueEditForm.category',
              defaultMessage: 'Category',
            })}
            {':'}
          </Text>
          <Select
            style={{ minWidth: 240 }}
            showSearch
            options={categoryOptions}
            value={issue.issue_category_id ?? null}
            onChange={(value) =>
              setIssue({ ...issue, issue_category_id: value })
            }
            placeholder={intl.formatMessage({
              id: 'incidentMgmt.issueEditForm.categoryPlaceholder',
              defaultMessage: 'Select a category',
            })}
            filterOption={filterOptionByLabel}
            disabled={readOnly || !isNewIssue}
          />
        </Flex>
      )}
      {!isEmpty(issue) && (
        <Flex vertical={false} align="center" gap="small">
          <Text strong>
            {intl.formatMessage({
              id: 'incidentMgmt.issueEditForm.responsible',
              defaultMessage: 'Responsible',
            })}
            {':'}
          </Text>
          <Select
            style={{ minWidth: 240 }}
            showSearch
            options={responsibleOptions}
            value={issue.responsible ?? null}
            onChange={(value) => setIssue({ ...issue, responsible: value })}
            placeholder={intl.formatMessage({
              id: 'incidentMgmt.issueEditForm.responsiblePlaceholder',
              defaultMessage: 'Select responsible user',
            })}
            filterOption={filterOptionByLabel}
            disabled={readOnly}
          />
        </Flex>
      )}
      {!isEmpty(issue) && (
        <Flex vertical={false} align="center" gap="small">
          <Text strong>
            {intl.formatMessage({
              id: 'incidentMgmt.issueEditForm.customerReference',
              defaultMessage: 'Customer Reference',
            })}
            {':'}
          </Text>
          <Input
            value={issue.customer_reference ?? ''}
            onChange={(evt) =>
              setIssue({ ...issue, customer_reference: evt.target.value })
            }
            disabled={readOnly}
          />
        </Flex>
      )}
      {isEmpty(issue) && (
        <p className="no-issues">
          {intl.formatMessage({
            id: 'incidentMgmt.issueEditForm.noIssues',
            defaultMessage: 'No Issues',
          })}
        </p>
      )}
      {!isHorizontal && !isEmpty(issue) && (
        <Collapse
          id="issue-details-accordian"
          items={items}
          defaultActiveKey="fault-details"
        />
      )}
      {isHorizontal && !isEmpty(issue) && (
        <Tabs
          type="card"
          items={items}
          activeKey={currentTab}
          onChange={setActiveTab}
        />
      )}
      {isHorizontal && !isEmpty(issue) && getButtons()}
    </Flex>
  )
}
